import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { X, Plus, Search } from 'lucide-react';
import { useApiService } from '../../services/apiService';

interface Product {
    id: number;
    itemNumber: string;
    name: string; // Added name property to match API response
    category: string;
    subCategory: string;
    sellingMarkets: string[];
}

interface ProductSelectorProps {
    selectedProducts: string[];
    onProductsChange: (products: string[]) => void;
}

const PAGE_SIZE = 10;

export function ProductSelector({ selectedProducts, onProductsChange }: ProductSelectorProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [category, setCategory] = useState('');
    const [subCategory, setSubCategory] = useState('');
    const [producingUnit, setProducingUnit] = useState('');
    const [sellingMarket, setSellingMarket] = useState('');
    const [page, setPage] = useState(1);
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Call useApiService at the top level, not inside useMemo
    const apiService = useApiService();

    // Fetch products from API
    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        setError(null);
        apiService.get<Product[]>('/products')
            .then((data) => {
                setProducts(data);
                setLoading(false);
            })
            .catch((err) => {
                setError('Failed to load products');
                setLoading(false);
            });
    }, [isOpen]); // Only depend on isOpen

    // Unique filter options
    const categories = Array.from(new Set(products.map(p => p.category)));
    const subCategories = Array.from(new Set(products.filter(p => !category || p.category === category).map(p => p.subCategory)));
    const producingUnits: string[] = []; // Explicitly type as string[]
    const sellingMarkets = Array.from(new Set(products.map(p => p.sellingMarkets).flat()));

    // Filtered products (do not remove selected from list)
    const filteredProducts = products.filter(product =>
        (searchTerm === '' ||
            (product.itemNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             product.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             product.subCategory?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        ) &&
        (!category || product.category === category) &&
        (!subCategory || product.subCategory === subCategory) &&
        (!producingUnit || false) && // producingUnit not present in API response
        (!sellingMarket || product.sellingMarkets.includes(sellingMarket))
    );

    // Paging logic
    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
    const pagedProducts = filteredProducts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // Windowed pagination logic
    const PAGINATION_WINDOW = 5;
    let pageNumbers: (number | string)[] = [];
    if (totalPages <= PAGINATION_WINDOW + 2) {
        // Show all pages if few enough
        pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
        // Always show first and last page
        const left = Math.max(2, page - Math.floor(PAGINATION_WINDOW / 2));
        const right = Math.min(totalPages - 1, page + Math.floor(PAGINATION_WINDOW / 2));
        pageNumbers = [1];
        if (left > 2) pageNumbers.push('...');
        for (let i = left; i <= right; i++) {
            pageNumbers.push(i);
        }
        if (right < totalPages - 1) pageNumbers.push('...');
        pageNumbers.push(totalPages);
    }

    const handleProductToggle = (product: string, checked: boolean) => {
        if (checked) {
            if (!selectedProducts.includes(product)) {
                onProductsChange([...selectedProducts, product]);
            }
        } else {
            onProductsChange(selectedProducts.filter(p => p !== product));
        }
    };

    const handleRemoveProduct = (product: string) => {
        onProductsChange(selectedProducts.filter(p => p !== product));
    };

    const handleClearSelection = () => {
        onProductsChange([]);
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    // Reset to page 1 when filters/search change
    useEffect(() => {
        setPage(1);
    }, [searchTerm, category, subCategory, producingUnit, sellingMarket]);

    return (
        <div className="space-y-3">
            <Label>Products</Label>
            {/* Selected Products Summary */}
            {selectedProducts.length > 0 && (
                <div className="space-y-2 flex items-center gap-4">
                    <div>
                        <p className="text-sm text-gray-600 mb-1">Selected products:</p>
                        <div className="flex flex-wrap gap-2">
                            {selectedProducts.map((product) => (
                                <Badge
                                    key={product}
                                    variant="secondary"
                                    className="flex items-center gap-1 pr-1"
                                >
                                    {product}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-4 w-4 p-0 hover:bg-gray-200"
                                        onClick={() => handleRemoveProduct(product)}
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                </Badge>
                            ))}
                        </div>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={handleClearSelection}>
                        Clear Selection
                    </Button>
                </div>
            )}
            {/* Add Products Section */}
            <div className="space-y-3">
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full justify-start"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Products
                </Button>
                {isOpen && (
                    <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                        {loading && <div className="text-center text-gray-500">Loading products...</div>}
                        {error && <div className="text-center text-red-500">{error}</div>}
                        {/* Filters */}
                        <div className="flex flex-wrap gap-2">
                            <div>
                                <Label className="text-xs">Category</Label>
                                <select className="block border rounded px-2 py-1" value={category} onChange={e => { setCategory(e.target.value); setSubCategory(''); }}>
                                    <option value="">All</option>
                                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label className="text-xs">SubCategory</Label>
                                <select className="block border rounded px-2 py-1" value={subCategory} onChange={e => setSubCategory(e.target.value)}>
                                    <option value="">All</option>
                                    {subCategories.map(sc => <option key={sc} value={sc}>{sc}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label className="text-xs">Producing Unit</Label>
                                <select className="block border rounded px-2 py-1" value={producingUnit} onChange={e => setProducingUnit(e.target.value)}>
                                    <option value="">All</option>
                                    {producingUnits.map(pu => <option key={pu} value={pu}>{pu}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label className="text-xs">Selling Market</Label>
                                <select className="block border rounded px-2 py-1" value={sellingMarket} onChange={e => setSellingMarket(e.target.value)}>
                                    <option value="">All</option>
                                    {sellingMarkets.map(sm => <option key={sm} value={sm}>{sm}</option>)}
                                </select>
                            </div>
                        </div>
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search products..."
                                className="pl-10"
                            />
                        </div>
                        {/* Product Table */}
                        <div>
                            <table className="min-w-full border text-left text-sm">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="p-2 font-semibold">Select</th>
                                        <th className="p-2 font-semibold">Id</th>
                                        <th className="p-2 font-semibold">Item Number</th>
                                        <th className="p-2 font-semibold">Name</th>
                                        <th className="p-2 font-semibold">Category</th>
                                        <th className="p-2 font-semibold">SubCategory</th>
                                        <th className="p-2 font-semibold">Producing Unit</th>
                                        <th className="p-2 font-semibold">Selling Market</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedProducts.map((product) => (
                                        <tr key={product.id} className="border-b last:border-b-0">
                                            <td className="p-2">
                                                <Checkbox
                                                    id={product.id.toString()}
                                                    checked={selectedProducts.includes(product.itemNumber)}
                                                    onCheckedChange={(checked) => handleProductToggle(product.itemNumber, !!checked)}
                                                />
                                            </td>
                                            <td className="p-2">{product.id}</td>
                                            <td className="p-2">{product.itemNumber}</td>
                                            <td className="p-2">
                                                <Label htmlFor={product.id.toString()} className="text-sm font-medium cursor-pointer">{product.name}</Label>
                                            </td>
                                            <td className="p-2">{product.category}</td>
                                            <td className="p-2">{product.subCategory}</td>
                                            <td className="p-2">N/A</td>
                                            <td className="p-2">{product.sellingMarkets.join(', ')}</td>
                                        </tr>
                                    ))}
                                    {pagedProducts.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan={8} className="text-center text-gray-500 py-2">
                                                No products found matching "{searchTerm}"
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-4">
                                    <Button size="sm" variant="outline" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                                        Previous
                                    </Button>
                                    {pageNumbers.map((p, idx) =>
                                        typeof p === 'number' ? (
                                            <Button
                                                key={p}
                                                size="sm"
                                                variant={page === p ? 'default' : 'outline'}
                                                onClick={() => handlePageChange(p)}
                                            >
                                                {p}
                                            </Button>
                                        ) : (
                                            <span key={"ellipsis-" + idx} className="px-2 text-gray-400">...</span>
                                        )
                                    )}
                                    <Button size="sm" variant="outline" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
                                        Next
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};